// Per-IP live enrichment for the IP and Domain Intelligence tool.
//
// PRIVACY NOTE: every function in this file SENDS THE QUERIED IP ADDRESS to a
// third party. That is the whole point of the module, and it is why the tool
// keeps it behind an explicit, per-lookup opt-in and names the recipients in
// the UI before the user clicks. The confidential half of the tool — VPN,
// datacenter, Tor and abuse-reputation verdicts — lives in ipIntelDatasets.js
// and never sends anything. Do not call anything here on the strength of a
// user merely pasting an address.
//
// All sources are free and keyless, and all were verified to send
// `Access-Control-Allow-Origin: *`. Every result carries its own source label
// and fetch time so provenance survives into the CSV export.

import { reverseArpaName, reversedOctets, isValidIpv4 } from './ipIntel';

const TIMEOUT_MS = 12000;

// The named recipients, shown in the UI before the user opts in. Keep this in
// sync with the disclosure banner, /privacy and /code-validation.
export const LIVE_SOURCES = [
  { host: 'rdap.org + the five RIRs', what: 'authoritative registration & abuse contact' },
  { host: 'dns.google', what: 'reverse DNS and BGP origin (Team Cymru)' },
  { host: 'ipwho.is, freeipapi.com, ipapi.is', what: 'approximate geolocation' },
  { host: 'internetdb.shodan.io', what: 'previously observed open ports' },
  { host: 'stat.ripe.net', what: 'abuse contact cross-check' },
];

async function getJson(url, signal) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  const onAbort = () => ctrl.abort();
  if (signal) {
    if (signal.aborted) ctrl.abort();
    signal.addEventListener('abort', onAbort);
  }
  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: ctrl.signal });
    if (!res.ok) {
      const err = new Error(`HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return await res.json();
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
}

// ── Own connection ───────────────────────────────────────────────────────────

// What the public internet sees when this browser connects. Any site you visit
// already learns this, so the check reveals nothing new to RIPEstat — but it is
// still a deliberate button press, never automatic.
export async function fetchOwnIp(signal) {
  const j = await getJson('https://stat.ripe.net/data/whats-my-ip/data.json', signal);
  const ip = j && j.data && j.data.ip;
  if (!ip) throw new Error('Could not determine your address');
  return ip;
}

// ── Free-tier budget ─────────────────────────────────────────────────────────

// The tightest ceiling across the providers: ipapi.is caps anonymous
// (keyless) use at 30 lookups per client IP per UTC day (verified against
// https://ipapi.is/free-tier.html — this was previously, and incorrectly,
// coded as 100). Past that it answers 429 for exactly 10 more requests, and a
// client that keeps sending past THAT gets firewalled by ipapi.is for a full
// 24 hours — which would break geolocation from this network entirely, not
// just for this tool. So this isn't just a display number: geoIpapiIs below
// refuses to place the call at all once the count is reached, rather than
// find out the hard way where the 10-request grace period ends. The other two
// providers are far more generous, so this is the number worth tracking.
// Counted per address, since one address costs one request at each provider.
export const DAILY_SOFT_LIMIT = 30;
const QUOTA_KEY = 'letools-iplookup-quota';

const utcDay = () => new Date().toISOString().slice(0, 10);

export function getLookupsToday() {
  try {
    const raw = JSON.parse(window.localStorage.getItem(QUOTA_KEY) || '{}');
    return raw.date === utcDay() ? (raw.count || 0) : 0;
  } catch {
    return 0; // private mode / storage blocked — just don't track
  }
}

export function recordLookups(n) {
  const next = getLookupsToday() + n;
  try {
    window.localStorage.setItem(QUOTA_KEY, JSON.stringify({ date: utcDay(), count: next }));
  } catch {
    // Counting is a convenience, never a blocker.
  }
  return next;
}

// ── RDAP ─────────────────────────────────────────────────────────────────────

const RIR_RDAP = [
  'https://rdap.arin.net/registry/ip/',
  'https://rdap.db.ripe.net/ip/',
  'https://rdap.apnic.net/ip/',
  'https://rdap.lacnic.net/rdap/ip/',
  'https://rdap.afrinic.net/rdap/ip/',
];

// vcardArray looks like ["vcard", [["fn",{},"text","Google LLC"], …]].
function vcardValue(entity, field) {
  const props = entity && entity.vcardArray && entity.vcardArray[1];
  if (!Array.isArray(props)) return '';
  for (const p of props) {
    if (Array.isArray(p) && p[0] === field) {
      const v = p[3];
      if (typeof v === 'string') return v;
      if (Array.isArray(v)) return v.filter(Boolean).join(', ');
    }
  }
  return '';
}

// Entities nest (an org holds an abuse contact), so walk the whole tree.
function findEntity(entities, role, depth = 0) {
  if (!Array.isArray(entities) || depth > 4) return null;
  for (const e of entities) {
    const roles = e.roles || [];
    if (roles.includes(role)) return e;
  }
  for (const e of entities) {
    const nested = findEntity(e.entities, role, depth + 1);
    if (nested) return nested;
  }
  return null;
}

function eventDate(events, action) {
  const hit = (events || []).find(e => e.eventAction === action);
  return hit ? String(hit.eventDate || '').slice(0, 10) : '';
}

// ARIN records carry no top-level `country`; the only country in them is the
// last line of the registrant's vCard address label.
function vcardAddressCountry(entity) {
  const props = entity && entity.vcardArray && entity.vcardArray[1];
  if (!Array.isArray(props)) return '';
  for (const p of props) {
    if (Array.isArray(p) && p[0] === 'adr') {
      const label = p[1] && p[1].label;
      if (typeof label === 'string' && label.trim()) {
        const lines = label.trim().split('\n').map(l => l.trim()).filter(Boolean);
        if (lines.length) return lines[lines.length - 1];
      }
    }
  }
  return '';
}

function parseRdap(json) {
  const entities = json.entities || [];
  const registrant = findEntity(entities, 'registrant')
    || findEntity(entities, 'administrative')
    || findEntity(entities, 'technical')
    || entities[0]
    || null;
  const abuse = findEntity(entities, 'abuse');

  return {
    netName: json.name || '',
    handle: json.handle || '',
    startAddress: json.startAddress || '',
    endAddress: json.endAddress || '',
    cidr: (json.cidr0_cidrs || []).map(c => `${c.v4prefix || c.v6prefix}/${c.length}`).join(', '),
    type: json.type || '',
    country: (json.country || (registrant ? vcardAddressCountry(registrant) : '') || '').toUpperCase(),
    parentHandle: json.parentHandle || '',
    registrantName: registrant ? (vcardValue(registrant, 'fn') || registrant.handle || '') : '',
    registrantKind: registrant ? vcardValue(registrant, 'kind') : '',
    abuseEmail: abuse ? vcardValue(abuse, 'email') : '',
    abuseName: abuse ? (vcardValue(abuse, 'fn') || abuse.handle || '') : '',
    abusePhone: abuse ? vcardValue(abuse, 'tel') : '',
    registered: eventDate(json.events, 'registration'),
    lastChanged: eventDate(json.events, 'last changed'),
    remarks: (json.remarks || [])
      .flatMap(r => r.description || [])
      .filter(Boolean)
      .slice(0, 4),
    port43: json.port43 || '',
  };
}

async function fetchRdap(ip, signal) {
  // rdap.org is the IANA bootstrap: it 302s to whichever RIR is authoritative,
  // and both hops send CORS, so a plain GET follows the redirect fine.
  try {
    return parseRdap(await getJson(`https://rdap.org/ip/${encodeURIComponent(ip)}`, signal));
  } catch (err) {
    if (err.name === 'AbortError') throw err;
  }
  // Bootstrap down or refusing: ask each RIR directly and take the first hit.
  for (const base of RIR_RDAP) {
    try {
      return parseRdap(await getJson(base + encodeURIComponent(ip), signal));
    } catch (err) {
      if (err.name === 'AbortError') throw err;
    }
  }
  throw new Error('No RIR returned a record');
}

// ── DNS over HTTPS ───────────────────────────────────────────────────────────

// Shared DNS-over-HTTPS entry point. Exported so the domain/email lookups use
// the same timeout, abort handling and resolver as the IP side.
export async function dnsQuery(name, type, signal) {
  const json = await getJson(`https://dns.google/resolve?name=${encodeURIComponent(name)}&type=${encodeURIComponent(type)}`, signal);
  return {
    status: json.Status,
    answers: (json.Answer || []).map(a => ({ type: a.type, data: String(a.data || ''), ttl: a.TTL })),
  };
}

async function dohTxt(name, signal) {
  const json = await getJson(`https://dns.google/resolve?name=${encodeURIComponent(name)}&type=TXT`, signal);
  return (json.Answer || [])
    .filter(a => a.type === 16)
    .map(a => String(a.data || '').replace(/^"|"$/g, ''));
}

async function fetchPtr(ip, signal) {
  const name = reverseArpaName(ip);
  if (!name) return { hostnames: [] }; // IPv6 nibble format not built out yet
  const json = await getJson(`https://dns.google/resolve?name=${encodeURIComponent(name)}&type=PTR`, signal);
  const hostnames = (json.Answer || [])
    .filter(a => a.type === 12)
    .map(a => String(a.data || '').replace(/\.$/, ''));
  return { hostnames };
}

// Team Cymru's DNS interface: the authoritative BGP view of who is announcing
// this address, which is a stronger claim than any geolocation vendor's guess.
async function fetchBgpOrigin(ip, signal) {
  const rev = reversedOctets(ip);
  if (!rev) return null;
  const txts = await dohTxt(`${rev}.origin.asn.cymru.com`, signal);
  if (!txts.length) return null;

  // "15169 | 8.8.8.0/24 | US | arin | 2023-12-28"
  const parts = txts[0].split('|').map(s => s.trim());
  const asn = (parts[0] || '').split(/\s+/)[0];
  const origin = {
    asn: asn ? `AS${asn}` : '',
    prefix: parts[1] || '',
    country: (parts[2] || '').toUpperCase(),
    registry: (parts[3] || '').toUpperCase(),
    allocated: parts[4] || '',
    asName: '',
  };

  if (asn) {
    try {
      const nameTxts = await dohTxt(`AS${asn}.asn.cymru.com`, signal);
      // "15169 | US | arin | 2000-03-30 | GOOGLE - Google LLC, US"
      if (nameTxts.length) {
        const np = nameTxts[0].split('|').map(s => s.trim());
        origin.asName = np[4] || '';
      }
    } catch (err) {
      if (err.name === 'AbortError') throw err;
    }
  }
  return origin;
}

// ── Geolocation (three providers, deliberately not reconciled) ───────────────

async function geoIpwhois(ip, signal) {
  const j = await getJson(`https://ipwho.is/${encodeURIComponent(ip)}`, signal);
  if (j.success === false) throw new Error((j.message) || 'Lookup failed');
  return {
    city: j.city || '', region: j.region || '', country: j.country || '',
    countryCode: j.country_code || '', postal: j.postal || '',
    lat: j.latitude, lon: j.longitude,
    isp: (j.connection && (j.connection.isp || j.connection.org)) || '',
    asn: j.connection && j.connection.asn ? `AS${j.connection.asn}` : '',
    domain: (j.connection && j.connection.domain) || '',
    timezone: (j.timezone && j.timezone.id) || '',
  };
}

async function geoFreeIpApi(ip, signal) {
  const j = await getJson(`https://free.freeipapi.com/api/json/${encodeURIComponent(ip)}`, signal);
  return {
    city: j.cityName || '', region: j.regionName || '', country: j.countryName || '',
    countryCode: j.countryCode || '', postal: j.zipCode || '',
    lat: j.latitude, lon: j.longitude,
    isp: j.asnOrganization || '',
    asn: j.asn ? `AS${j.asn}` : '',
    timezone: (j.timeZones && j.timeZones[0]) || '',
    // freeipapi exposes a proxy hint on the free tier; the offline lists remain
    // the primary signal, this is only a cross-check.
    isProxy: typeof j.isProxy === 'boolean' ? j.isProxy : null,
  };
}

async function geoIpapiIs(ip, signal) {
  // Refuse rather than find out where the 10-request grace period past the
  // daily cap ends — that road leads to ipapi.is firewalling this network for
  // 24 hours. The count is a local estimate (see recordLookups), so this is a
  // safety margin, not a guarantee the real server-side count is identical.
  if (getLookupsToday() >= DAILY_SOFT_LIMIT) {
    throw new Error(`Daily anonymous quota (${DAILY_SOFT_LIMIT}) reached — skipped to avoid a 24h block from ipapi.is`);
  }
  const j = await getJson(`https://api.ipapi.is/?q=${encodeURIComponent(ip)}`, signal);
  return {
    city: j.city || '', region: j.region || '', country: j.country || '',
    lat: j.lat, lon: j.lon,
    isp: j.company || '',
    asn: (j.asn || '').split(' ')[0] || '',
    timezone: j.timezone || '',
    isBogon: j.is_bogon === true,
  };
}

const GEO_PROVIDERS = [
  { id: 'ipwho.is',      fn: geoIpwhois },
  { id: 'freeipapi.com', fn: geoFreeIpApi },
  { id: 'ipapi.is',      fn: geoIpapiIs },
];

// Do NOT pick a winner. Providers genuinely disagree — on one Cloudflare
// address these three returned Los Angeles, Toronto and Santa Ana — and
// silently choosing one would manufacture false confidence in a location that
// may end up in an affidavit.
export function geoAgreement(results) {
  const ok = results.filter(r => r.data);
  if (ok.length < 2) return { level: 'insufficient', countries: [], cities: [] };
  // Compare on the full country NAME, which all three providers return. Mixing
  // in countryCode (which only some return) made "US" and "United States" look
  // like a disagreement and flagged every lookup as a conflict.
  const countries = [...new Set(ok.map(r => (r.data.country || '').trim().toUpperCase()).filter(Boolean))];
  const cities = [...new Set(ok.map(r => (r.data.city || '').toLowerCase()).filter(Boolean))];
  if (countries.length > 1) return { level: 'country-conflict', countries, cities };
  if (cities.length > 1) return { level: 'city-conflict', countries, cities };
  return { level: 'agree', countries, cities };
}

// ── Named VPN provider detection ─────────────────────────────────────────────

// A provider that runs its own ASN shows up by name in public registry data —
// e.g. AS199218 is literally registered as "ProtonVPN-2 - Proton AG, CH", so
// Team Cymru's BGP-origin lookup names it with no curated list required. This
// only catches providers operating their own address space; a VPN running on
// IPs leased from a generic host (M247, DigitalOcean, etc.) shows the host's
// name instead and there's no public record of who's renting from them — that
// mapping only exists in paid, continuously-verified databases like MaxMind's.
const KNOWN_VPN_NAME_PATTERNS = [
  { name: 'ProtonVPN',                  re: /proton\s*vpn/i },
  { name: 'NordVPN',                    re: /nordvpn|nord\s*security/i },
  { name: 'Mullvad VPN',                re: /mullvad/i },
  { name: 'ExpressVPN',                 re: /expressvpn/i },
  { name: 'Surfshark',                  re: /surfshark/i },
  { name: 'Private Internet Access',    re: /private internet access/i },
  { name: 'IPVanish',                   re: /ipvanish/i },
  { name: 'CyberGhost',                 re: /cyberghost/i },
  { name: 'Windscribe',                 re: /windscribe/i },
  { name: 'PureVPN',                    re: /purevpn/i },
  { name: 'TunnelBear',                 re: /tunnelbear/i },
  { name: 'Hotspot Shield',             re: /hotspot\s*shield|aura\s*sub/i },
  { name: 'hide.me',                    re: /\bhide\.?me\b/i },
  { name: 'VyprVPN',                    re: /vyprvpn/i },
  { name: 'Atlas VPN',                  re: /atlas\s*vpn/i },
  { name: 'TorGuard',                   re: /torguard/i },
  { name: 'Astrill VPN',                re: /astrill/i },
  { name: 'StrongVPN',                  re: /strongvpn/i },
  { name: 'ZenMate',                    re: /zenmate/i },
];

// Checks the fields a live lookup already fetched — no extra request. Returns
// { provider, source, matchedText } for the first hit, or null.
export function detectNamedVpnProvider(live) {
  if (!live) return null;
  const fields = [
    ['BGP AS name', live.bgp && live.bgp.data && live.bgp.data.asName],
    ['RDAP registrant', live.rdap && live.rdap.data && live.rdap.data.registrantName],
    ['RDAP network name', live.rdap && live.rdap.data && live.rdap.data.netName],
  ];
  for (const [source, value] of fields) {
    if (!value) continue;
    const hit = KNOWN_VPN_NAME_PATTERNS.find(p => p.re.test(value));
    if (hit) return { provider: hit.name, source, matchedText: value };
  }
  return null;
}

// ── Shodan InternetDB & RIPEstat ─────────────────────────────────────────────

async function fetchShodan(ip, signal) {
  try {
    const j = await getJson(`https://internetdb.shodan.io/${encodeURIComponent(ip)}`, signal);
    return {
      observed: true,
      ports: j.ports || [],
      hostnames: j.hostnames || [],
      cpes: j.cpes || [],
      vulns: j.vulns || [],
      tags: j.tags || [],
    };
  } catch (err) {
    // 404 is a real answer: Shodan has simply never seen this host.
    if (err.status === 404) return { observed: false, ports: [], hostnames: [], cpes: [], vulns: [], tags: [] };
    throw err;
  }
}

async function fetchRipestatAbuse(ip, signal) {
  const j = await getJson(`https://stat.ripe.net/data/abuse-contact-finder/data.json?resource=${encodeURIComponent(ip)}`, signal);
  const d = j.data || {};
  return {
    contacts: d.abuse_contacts || [],
    authoritativeRir: (d.authoritative_rir || '').toUpperCase(),
  };
}

// ── Orchestration ────────────────────────────────────────────────────────────

function settle(source, promise) {
  const startedAt = Date.now();
  return promise.then(
    data => ({ source, data, error: null, fetchedAt: Date.now() }),
    err => ({
      source,
      data: null,
      error: err.name === 'AbortError' ? 'Cancelled' : (err.message || 'Failed'),
      fetchedAt: startedAt,
    }),
  );
}

// Fans out to every source at once. One provider failing never sinks the row —
// a partial answer is still an answer, and the UI shows which pieces are missing.
export async function enrichIp(ip, { signal } = {}) {
  const [rdap, ptr, bgp, shodan, ripestat, ...geo] = await Promise.all([
    settle('RDAP', fetchRdap(ip, signal)),
    settle('dns.google (PTR)', fetchPtr(ip, signal)),
    settle('Team Cymru via dns.google', isValidIpv4(ip) ? fetchBgpOrigin(ip, signal) : Promise.resolve(null)),
    settle('Shodan InternetDB', fetchShodan(ip, signal)),
    settle('RIPEstat', fetchRipestatAbuse(ip, signal)),
    ...GEO_PROVIDERS.map(p => settle(p.id, p.fn(ip, signal))),
  ]);

  return { ip, rdap, ptr, bgp, shodan, ripestat, geo, enrichedAt: Date.now() };
}

// Runs a batch with bounded concurrency so we stay polite to free services.
// `onResult` fires per IP so rows fill in progressively instead of all at once.
export async function enrichBatch(ips, { signal, concurrency = 4, onResult } = {}) {
  const queue = [...ips];
  const results = new Map();

  const worker = async () => {
    for (;;) {
      if (signal && signal.aborted) return;
      const ip = queue.shift();
      if (!ip) return;
      try {
        const result = await enrichIp(ip, { signal });
        results.set(ip, result);
        if (onResult) onResult(ip, result);
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));
  return results;
}

// The authoritative answer to "was this IP a Tor exit at time X" — Onionoo only
// describes relays running now, so a historical question has to go to Tor
// Metrics. It's HTML-only with no CORS, so we hand the user a prefilled link
// rather than trying to fetch it.
export function exoneratorUrl(ip, date) {
  const day = (date instanceof Date ? date : new Date()).toISOString().slice(0, 10);
  return `https://metrics.torproject.org/exonerator.html?ip=${encodeURIComponent(ip)}&timestamp=${day}&lang=en`;
}
